import { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { floorplanRooms, zoneColors, zoneLabels } from "../data/floorplan";
import { equipmentTypes, sensorTypes, modeColors, modeList, sensorSourceList } from "../data/equipmentTypes";
import {
  loadState,
  persistState,
  pushRemoteState,
  ensureControls,
  addEquipment as addEq,
  removeEquipment as removeEq,
  setEquipmentMode,
  setEquipmentService,
  clearEquipmentService,
  getDefaultSensorSource,
  attachSensor as attachS,
  detachSensor as detachS,
  getEligibleSensors,
} from "../state/roomState";
import MiniMap from "./MiniMap";

/* ── Mode chip for the legend bar ── */
function ModeChip({ mode, active, count, onClick }) {
  const c = modeColors[mode];
  return (
    <button
      onClick={onClick}
      className="room-mode-chip"
      style={{
        background: active ? c.bg : "rgba(15,23,42,0.04)",
        color: active ? c.text : "#64748b",
        border: `2px solid ${active ? c.border : "transparent"}`,
        boxShadow: active ? `0 0 18px ${c.glow}, inset 0 1px 0 rgba(255,255,255,0.06)` : "none",
      }}
    >
      <span className="room-mode-dot" style={{ background: c.dot, boxShadow: active ? `0 0 8px ${c.dot}` : "none" }} />
      {c.label}
      <span className="room-mode-count" style={{ background: active ? `${c.border}33` : "rgba(0,0,0,0.06)", color: active ? c.text : "#94a3b8" }}>
        {count}
      </span>
    </button>
  );
}

/* ── Service config expandable panel ── */
function ServicePanel({ eq, onSave, onClear }) {
  const defaults = {
    sensorSource: getDefaultSensorSource(eq.type),
    itemNumber: "",
    startDiff: 0,
    stopDiff: 0,
    delay: 0,
    scheduleStart: "06:00",
    scheduleEnd: "18:00",
  };
  const [form, setForm] = useState(eq.service || defaults);
  const [editing, setEditing] = useState(!eq.service);

  const update = (key, val) => setForm({ ...form, [key]: val });

  const handleSave = () => {
    onSave(eq.id, form);
    setEditing(false);
  };

  if (!editing && eq.service) {
    return (
      <div className="service-panel">
        <div className="service-panel-summary">
          <span className="service-panel-sensor">{sensorTypes[eq.service.sensorSource]?.label || eq.service.sensorSource}</span>
          <span className="service-panel-item">#{eq.service.itemNumber || "—"}</span>
          <span className="service-panel-diff">+{eq.service.startDiff}/-{eq.service.stopDiff}</span>
          <span className="service-panel-delay">{eq.service.delay}s delay</span>
        </div>
        <div className="service-panel-schedule">
          {eq.service.scheduleStart} → {eq.service.scheduleEnd}
        </div>
        <div className="service-panel-actions">
          <button className="service-btn-edit" onClick={() => setEditing(true)}>Edit</button>
          <button className="service-btn-clear" onClick={() => onClear(eq.id)}>Remove</button>
        </div>
      </div>
    );
  }

  return (
    <div className="service-panel service-panel-form">
      <div className="service-field">
        <label>Sensor Source</label>
        <select value={form.sensorSource} onChange={(e) => update("sensorSource", e.target.value)}>
          {sensorSourceList.map((s) => (
            <option key={s} value={s}>{sensorTypes[s]?.label || s}</option>
          ))}
        </select>
      </div>
      <div className="service-field">
        <label>Item #</label>
        <input type="text" value={form.itemNumber} onChange={(e) => update("itemNumber", e.target.value)} placeholder="CU-001" />
      </div>
      <div className="service-field-row">
        <div className="service-field">
          <label>Start Diff</label>
          <input type="number" value={form.startDiff} onChange={(e) => update("startDiff", Number(e.target.value))} />
        </div>
        <div className="service-field">
          <label>Stop Diff</label>
          <input type="number" value={form.stopDiff} onChange={(e) => update("stopDiff", Number(e.target.value))} />
        </div>
        <div className="service-field">
          <label>Delay (s)</label>
          <input type="number" value={form.delay} onChange={(e) => update("delay", Number(e.target.value))} />
        </div>
      </div>
      <div className="service-field-row">
        <div className="service-field">
          <label>Schedule Start</label>
          <input type="time" value={form.scheduleStart} onChange={(e) => update("scheduleStart", e.target.value)} />
        </div>
        <div className="service-field">
          <label>Schedule End</label>
          <input type="time" value={form.scheduleEnd} onChange={(e) => update("scheduleEnd", e.target.value)} />
        </div>
      </div>
      <div className="service-panel-actions">
        <button className="service-btn-save" onClick={handleSave}>Save Service</button>
        {eq.service && <button className="service-btn-clear" onClick={() => { onClear(eq.id); setEditing(false); }}>Remove</button>}
      </div>
    </div>
  );
}

/* ── Single equipment card in the diagram ── */
function DiagramCard({ eq, onModeChange, onRemove, onServiceSave, onServiceClear }) {
  const mode = eq.mode || "auto";
  const c = modeColors[mode];
  const typeDef = equipmentTypes[eq.type];
  const hasService = !!eq.service;
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`diagram-card ${hasService ? "diagram-card-configured" : "diagram-card-unconfigured"}`}
      style={{
        background: `linear-gradient(135deg, ${c.bg}, ${c.bg}dd)`,
        border: `1.5px solid ${hasService ? c.border : c.border + "55"}`,
        boxShadow: hasService
          ? `0 0 24px ${c.glow}, 0 0 8px rgba(6,182,212,0.2), 0 4px 16px rgba(0,0,0,0.2)`
          : `0 0 24px ${c.glow}, 0 4px 16px rgba(0,0,0,0.2)`,
      }}
    >
      {/* Status indicator bar */}
      <div className="diagram-card-indicator" style={{ background: c.border, boxShadow: `0 0 12px ${c.dot}` }} />

      <div className="diagram-card-body">
        <div className="diagram-card-header">
          <div>
            <span className="diagram-card-type" style={{ color: `${c.text}99` }}>{typeDef?.label || eq.type}</span>
            <h4 className="diagram-card-name" style={{ color: c.text }}>
              {eq.label}
              {hasService
                ? <span className="service-badge configured" title="Service configured">⚡</span>
                : <span className="service-badge unconfigured" title="No service configured">○</span>
              }
            </h4>
          </div>
          <button className="diagram-card-remove" onClick={() => onRemove(eq.id)} title="Remove">×</button>
        </div>

        {/* Sensors attached */}
        {eq.sensors.length > 0 && (
          <div className="diagram-card-sensors">
            {eq.sensors.map((s) => (
              <div key={s.type} className="diagram-sensor-tag" style={{ borderColor: `${c.border}44`, color: c.text }}>
                <span className="diagram-sensor-dot" style={{ background: "#06b6d4" }} />
                {sensorTypes[s.type]?.label || s.type}
              </div>
            ))}
          </div>
        )}

        {/* Mode toggle */}
        <div className="diagram-mode-toggle">
          {modeList.map((m) => {
            const mc = modeColors[m];
            const isActive = mode === m;
            return (
              <button
                key={m}
                className={`diagram-mode-btn ${isActive ? "active" : ""}`}
                onClick={() => onModeChange(eq.id, m)}
                style={isActive ? {
                  background: mc.border,
                  color: "#fff",
                  boxShadow: `0 0 12px ${mc.glow}`,
                } : {}}
              >
                {m === "manual_on" ? "M-ON" : m === "manual_off" ? "M-OFF" : "AUTO"}
              </button>
            );
          })}
        </div>

        {/* Service expand toggle */}
        <button
          className={`service-expand-btn ${hasService ? "configured" : ""}`}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? "▲ Hide Service" : hasService ? "▼ Service Details" : "▼ Configure Service"}
        </button>
      </div>

      {/* Expandable service panel */}
      {expanded && (
        <ServicePanel eq={eq} onSave={onServiceSave} onClear={onServiceClear} />
      )}
    </div>
  );
}

/* ── Add equipment card ── */
function AddCard({ onAdd }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="diagram-add-card">
      {open ? (
        <div className="diagram-add-options">
          {Object.entries(equipmentTypes).map(([key, def]) => (
            <button
              key={key}
              className="diagram-add-option"
              onClick={() => { onAdd(key); setOpen(false); }}
            >
              + {def.label}
            </button>
          ))}
          <button className="diagram-add-cancel" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      ) : (
        <button className="diagram-add-trigger" onClick={() => setOpen(true)}>
          <span className="diagram-add-icon">+</span>
          <span>Add Equipment</span>
        </button>
      )}
    </div>
  );
}


export default function RoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState(() => loadState());
  const [activeMode, setActiveMode] = useState(null);

  const fpRoom = floorplanRooms.find((r) => r.id === roomId);
  const room = rooms.find((r) => r.id === roomId);

  const persist = useCallback(() => {
    persistState(rooms);
    pushRemoteState(rooms);
    setRooms([...rooms]);
  }, [rooms]);

  if (!fpRoom) {
    return (
      <div className="app">
        <div className="panel section">
          <h2>Room not found</h2>
          <p style={{ color: "var(--muted)", marginTop: 8 }}>
            No room with ID "{roomId}" exists in the facility map.
          </p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate("/")}>
            Back to Floor Plan
          </button>
        </div>
      </div>
    );
  }

  const colors = zoneColors[fpRoom.zone];
  const zoneName = zoneLabels[fpRoom.zone];
  const controls = room ? ensureControls(room) : { equipment: [], standalones: {}, outputs: [] };
  const equipment = controls.equipment.map((eq) => ({ ...eq, mode: eq.mode || "auto" }));

  // Mode counts
  const modeCounts = { manual_on: 0, manual_off: 0, auto: 0 };
  equipment.forEach((eq) => { modeCounts[eq.mode] = (modeCounts[eq.mode] || 0) + 1; });

  // Filter by active mode
  const filtered = activeMode ? equipment.filter((eq) => eq.mode === activeMode) : equipment;

  const handleModeChange = (eqId, mode) => {
    if (!room) return;
    setEquipmentMode(room, eqId, mode);
    persist();
  };

  const handleAdd = (type) => {
    if (!room) return;
    addEq(room, type);
    persist();
  };

  const handleRemove = (eqId) => {
    if (!room) return;
    removeEq(room, eqId);
    persist();
  };

  const handleServiceSave = (eqId, config) => {
    if (!room) return;
    setEquipmentService(room, eqId, config);
    persist();
  };

  const handleServiceClear = (eqId) => {
    if (!room) return;
    clearEquipmentService(room, eqId);
    persist();
  };

  return (
    <div className="app room-page-v2">
      {/* Breadcrumb */}
      <div className="room-breadcrumb">
        <button className="btn btn-ghost" onClick={() => navigate("/")}>← Floor Plan</button>
        <span className="room-breadcrumb-sep">/</span>
        <span className="room-breadcrumb-zone" style={{ color: colors.text }}>{zoneName}</span>
        <span className="room-breadcrumb-sep">/</span>
        <span className="room-breadcrumb-name">{fpRoom.label}</span>
      </div>

      {/* Hero header with minimap */}
      <div
        className="panel section room-v2-hero"
        style={{
          borderLeft: `5px solid ${colors.stroke}`,
          background: `linear-gradient(135deg, ${colors.fill}, rgba(255,252,247,0.92))`,
        }}
      >
        <div className="room-v2-hero-left">
          <div className="pill" style={{ background: colors.fill, color: colors.text, border: `1px solid ${colors.stroke}` }}>
            {zoneName}
          </div>
          <h1 className="room-v2-title">{fpRoom.label}</h1>
          <p className="room-v2-subtitle">
            {equipment.length} equipment unit{equipment.length !== 1 ? "s" : ""} configured
          </p>
        </div>
        <div className="room-v2-hero-map">
          <MiniMap activeRoomId={fpRoom.id} />
        </div>
      </div>

      {/* Mode legend — replaces flower/veg/dry */}
      <div className="room-mode-bar">
        <ModeChip
          mode="manual_on"
          active={activeMode === "manual_on"}
          count={modeCounts.manual_on}
          onClick={() => setActiveMode(activeMode === "manual_on" ? null : "manual_on")}
        />
        <ModeChip
          mode="manual_off"
          active={activeMode === "manual_off"}
          count={modeCounts.manual_off}
          onClick={() => setActiveMode(activeMode === "manual_off" ? null : "manual_off")}
        />
        <ModeChip
          mode="auto"
          active={activeMode === "auto"}
          count={modeCounts.auto}
          onClick={() => setActiveMode(activeMode === "auto" ? null : "auto")}
        />
        {activeMode && (
          <button className="room-mode-clear" onClick={() => setActiveMode(null)}>
            Show All
          </button>
        )}
      </div>

      {/* Equipment diagram grid */}
      <div className="room-diagram-section">
        <div className="room-diagram-head">
          <h2>Equipment Diagram</h2>
          <span className="room-diagram-count">
            {filtered.length} of {equipment.length} shown
          </span>
        </div>

        {filtered.length === 0 && equipment.length === 0 ? (
          <div className="room-diagram-empty">
            <div className="room-diagram-empty-icon">⚡</div>
            <p>No equipment configured for {fpRoom.label}.</p>
            <p className="room-diagram-empty-hint">Add cool units, dehumidifiers, or foggers to start building the diagram.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="room-diagram-empty">
            <p>No equipment in <strong>{modeColors[activeMode]?.label}</strong> mode.</p>
          </div>
        ) : null}

        <div className="room-diagram-grid">
          {filtered.map((eq) => (
            <DiagramCard
              key={eq.id}
              eq={eq}
              onModeChange={handleModeChange}
              onRemove={handleRemove}
              onServiceSave={handleServiceSave}
              onServiceClear={handleServiceClear}
            />
          ))}
          <AddCard onAdd={handleAdd} />
        </div>
      </div>

      {/* Standalone sensors & outputs summary */}
      {(controls.standalones || controls.outputs?.length > 0) && (
        <div className="room-peripherals">
          <div className="panel section room-peripheral-card">
            <h3>Standalone Sensors</h3>
            <div className="room-peripheral-list">
              {controls.standalones?.damatexCage && (
                <div className={`room-peripheral-item ${controls.standalones.damatexCage.present ? "active" : ""}`}>
                  <span className="room-peripheral-dot" style={{ background: controls.standalones.damatexCage.present ? "#06b6d4" : "#94a3b8" }} />
                  Damatex Cage
                  <span className="room-peripheral-status">{controls.standalones.damatexCage.present ? "Installed" : "Not installed"}</span>
                </div>
              )}
              {controls.standalones?.lightIntrusion && (
                <div className={`room-peripheral-item ${controls.standalones.lightIntrusion.present ? "active" : ""}`}>
                  <span className="room-peripheral-dot" style={{ background: controls.standalones.lightIntrusion.present ? "#f59e0b" : "#94a3b8" }} />
                  Light Intrusion
                  <span className="room-peripheral-status">{controls.standalones.lightIntrusion.present ? "Active" : "Inactive"}</span>
                </div>
              )}
              {controls.standalones?.rotorFlow && (
                <div className={`room-peripheral-item ${controls.standalones.rotorFlow.present ? "active" : ""}`}>
                  <span className="room-peripheral-dot" style={{ background: controls.standalones.rotorFlow.present ? "#8b5cf6" : "#94a3b8" }} />
                  Rotor Flow
                  <span className="room-peripheral-status">{controls.standalones.rotorFlow.present ? "Active" : "Inactive"}</span>
                </div>
              )}
            </div>
          </div>

          <div className="panel section room-peripheral-card">
            <h3>Outputs</h3>
            <div className="room-peripheral-list">
              {controls.outputs?.map((out) => (
                <div key={out.id} className="room-peripheral-item active">
                  <span className="room-peripheral-dot" style={{ background: "#10b981" }} />
                  {out.label}
                  <span className="room-peripheral-status">Connected</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Link to full controls page */}
      <div style={{ textAlign: "center", padding: "8px 0 24px" }}>
        <button
          className="btn btn-secondary"
          onClick={() => navigate(`/controls/${encodeURIComponent(fpRoom.id)}`)}
          style={{ fontSize: 14 }}
        >
          Open Full Controls Panel →
        </button>
      </div>
    </div>
  );
}
